// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import {
  IAction,
  ActionType,
  raiseAction,
  IActionTypeSetPeriod,
  ActionGraphCommand,
} from "actions";
import { TrendMonitor as ITrendMonitor } from "dbtypes";
import { TimePeriod } from "types";
import { IState } from "states/states";
import { getSiteSelection, toDropdownItems } from "guiutils";

import Dropdown from "view/comp/Dropdown";

interface ITrendMonitorSelectExtProps {
  className?: string;
  dashboardIndex?: number;
}

interface ITrendMonitorSelectProps {
  selectedMonitor: number | undefined;
  trendMonitors: ITrendMonitor[];
}

const mapStateToProps = (state: IState, ownProps: ITrendMonitorSelectExtProps) => {
  const site = state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);
  const props: ITrendMonitorSelectProps = {
    selectedMonitor: getSiteSelection(state)?.trendMonitor,
    trendMonitors: site?.cloudSettings?.trendMonitor ?? [],
  };
  return props;
};

interface ITrendMonitorSelectDispatch {
  onTrendMonitorChanged: (trendMonitors: ITrendMonitor[], value: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITrendMonitorSelectDispatch = {
    onTrendMonitorChanged: (trendMonitors: ITrendMonitor[], value: number) => {
      dispatch(raiseAction(ActionType.ACT_SELECT_TRENDMONITOR, value as number));
      // also update timePeriod
      const selectedTrendMonitor = trendMonitors[value as number];
      const payload: IActionTypeSetPeriod = {
        identifier: "trendPeriod",
        command: ActionGraphCommand.SET_PERIOD,
        period: selectedTrendMonitor.period as unknown as TimePeriod,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_PERIOD, payload));
      // load data
      dispatch(raiseAction(ActionType.ACT_UPDATE_TREND));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectCaption: {
    id: "Select trend monitor",
  },
});

class TrendMonitorSelectComponent extends React.Component<
  ITrendMonitorSelectExtProps &
    ITrendMonitorSelectProps &
    ITrendMonitorSelectDispatch &
    WrappedComponentProps
> {
  public render() {
    const ids = Array.from(Array(this.props.trendMonitors.length).keys())
      .filter((curId) => this.props.trendMonitors[curId].isActive)
      .sort((a, b) =>
        this.props.trendMonitors[a].title.localeCompare(this.props.trendMonitors[b].title),
      );
    const items = toDropdownItems(
      ids,
      "selectedTrendMonitor",
      (name, value) => this.props.onTrendMonitorChanged(this.props.trendMonitors, value),
      (id) => this.props.trendMonitors[id].title,
    );

    return (
      <div id="trendmonitorselect">
        <Dropdown
          items={items}
          caption={this.props.intl.formatMessage(messages.selectCaption)}
          curItem={
            this.props.trendMonitors[this.props.selectedMonitor!]?.title ??
            this.props.intl.formatMessage(messages.selectCaption)
          }
        />
      </div>
    );
  }
}

const TrendMonitorSelect = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TrendMonitorSelectComponent);

export default injectIntl(TrendMonitorSelect);
